/**
 * One global-search hit: icon, label, and navigation (opens the matching sidebar group).
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { LabelBadge } from '@/components/common/LabelBadge';
import {
  findSidebarGroupForRoute,
  resolveSearchResultIcon,
} from '@/components/search/search-utils';
import type { SearchResultItem as SearchResultItemType, SearchRole } from '@/types/search.types';
import { useLayoutStore } from '@/stores/layoutStore';
import { useInterfaceElementsStore } from '@/stores/interfaceElementsStore';
import { useMode } from '@/contexts/ModeContext';
import { getRoleColorCss, getRoleLabel } from '@/utils/role-label-utils';

/** Props for {@link SearchResultItem}. */
interface SearchResultItemProps {
  result: SearchResultItemType;
  onClick?: () => void;
  isActive?: boolean;
}

/** One global-search hit: icon, title, and optional subtitle. */
export const SearchResultItem: React.FC<SearchResultItemProps> = ({
  result,
  onClick,
  isActive = false,
}) => {
  const navigate = useNavigate();
  const { mode } = useMode();
  const { setOpenMenus } = useLayoutStore();
  const sidebarElements = useInterfaceElementsStore(
    (state) => state.elementsByInterface.sidebar ?? [],
  );
  const Icon = resolveSearchResultIcon(result.icon);

  /** One role chip for a user search hit. */
  function renderRoleBadge(role: SearchRole): React.ReactNode {
    return (
      <LabelBadge
        key={role.code}
        label={getRoleLabel(role.code, role.label)}
        variant="outlined"
        color={getRoleColorCss(role.code)}
        size="sm"
        className="!min-w-0"
      />
    );
  }

  function renderRoleBadges(): React.ReactNode {
    const roles = result.roles;
    if (!roles || roles.length === 0) return null;
    const nodes: React.ReactNode[] = [];
    for (const role of roles) {
      nodes.push(renderRoleBadge(role));
    }
    return <div className="global-search-result-roles">{nodes}</div>;
  }

  /** Role chips when the hit has roles; otherwise the plain sublabel. */
  function renderSublabel(): React.ReactNode {
    const badges = renderRoleBadges();
    if (badges) return badges;
    if (!result.sublabel) return null;
    return <div className="global-search-result-sublabel">{result.sublabel}</div>;
  }

  /**
   * Sync sidebar group state with the destination route:
   * find the sidebar element whose routePath matches and open its group.
   */
  const handleClick = () => {
    const matchingEl = findSidebarGroupForRoute(result.route, sidebarElements);
    const activeMode = mode ?? 'in_person';
    if (matchingEl?.groupLabel) {
      setOpenMenus([matchingEl.groupLabel], activeMode);
    }

    void navigate(result.route);
    onClick?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn('global-search-result-item', isActive && 'active')}
    >
      <div className={cn('global-search-result-icon', `icon-${result.iconColor}`)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="global-search-result-content">
        <div className="global-search-result-label">{result.label}</div>
        {renderSublabel()}
      </div>
    </button>
  );
};
